import { Location } from '@angular/common';
import { HttpBackend, provideHttpClient } from '@angular/common/http';
import { ApplicationConfig, importProvidersFrom, provideZoneChangeDetection } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { provideRouter, withDisabledInitialNavigation } from '@angular/router';

import dayjs from 'dayjs';
import 'dayjs/locale/th';
import buddhistEra from 'dayjs/plugin/buddhistEra';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import utc from 'dayjs/plugin/utc';

import { CacheMechanism, LocalizeParser, LocalizeRouterSettings, ManualParserLoader, withLocalizeRouter } from '@gilsdav/ngx-translate-router';
import { MissingTranslationHandler, provideTranslateService, TranslateLoader, TranslateService } from '@ngx-translate/core';
import { MultiTranslateHttpLoader } from 'ngx-translate-multi-http-loader';

import { CoreRouteService, CustomApiErrorHandlerService, GlobalDataService } from '@core-service';
import { environment } from '@env';

import { CoreApiClientConfig, CoreApiErrorHandlerService, CoreAuthInterceptorConfig, CoreAuthResponseKey, CoreAuthServiceConfig, CoreStorageConfig, CoreTranslateService, coreUserApiClientConfigToken, coreUserAuthInterceptorConfigToken, coreUserAuthResponseKeyToken, coreUserAuthServiceConfigToken, coreUserStorageConfigToken, dateRequestFormatter, dateResponseReviver, errorHandler, LibMissingTranslationHandler, PopupService } from 'pn-core-code';

import { ConfirmationService, MessageService } from 'primeng/api';

import { routes } from './app.routes';

dayjs.locale('th')
dayjs.extend(utc);
dayjs.extend(buddhistEra);
dayjs.extend(isSameOrAfter)
dayjs.extend(isSameOrBefore)

export function initApiClientConfig(): CoreApiClientConfig {
  return {
    pageHeaderResponseKeys: {
      pageCount: 'X-Paging-PageCount',
      totalCount: 'X-Paging-TotalRecordCount',
    },
    dateRequestFormatter,
    dateResponseReviver,
    loginScreenRoute: environment.loginScreenRoute,
    errorHandler: errorHandler
  };
}

export function initAuthServiceConfig(): CoreAuthServiceConfig {
  return {
    tokenUrl: environment.tokenUrl,
    refreshTokenUrl: `${environment.apiBaseUrl}/auth/refresh-token`,
    isFormData: false,
    isJWT: true,
    subSystemCode: 'web',
  };
}

export function initAuthInterceptorConfigToken(): CoreAuthInterceptorConfig {
  return {
    autoRefreshToken: true,
    loginScreenUrl: environment.loginScreenUrl,
    loginScreenRoute: environment.loginScreenRoute,
  };
}

const authResponseKey: CoreAuthResponseKey = {
  access_token: 'token',
};

export function HttpLoaderFactory(_httpBackend: HttpBackend) {
  return new MultiTranslateHttpLoader(_httpBackend, [
    '/default-lib-i18n/',
    '/i18n/'
  ]); // /i18n/core/ on angular >= v18 with the new public logic
}

export function initStorageConfig(): CoreStorageConfig {
  return {
    storagePrefix: environment.storagePrefix,
    usePersistentAsDefault: true,
  };
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(
      routes,
      withDisabledInitialNavigation(),
      withLocalizeRouter(routes, { // <--
        parser: {
          provide: LocalizeParser,
          useFactory: (translate: TranslateService, location: Location, settings: LocalizeRouterSettings) => new ManualParserLoader(translate, location, settings, ['th', 'en']),
          deps: [TranslateService, Location, LocalizeRouterSettings],
        },
        initialNavigation: true,
        defaultLangFunction: (languages: string[], cachedLang?: string, browserLang?: string) => {
          return cachedLang || 'th';
        },
        cacheMechanism: CacheMechanism.Cookie
      })
    ),
    provideAnimationsAsync(),
    importProvidersFrom([BrowserModule, BrowserAnimationsModule]),
    provideHttpClient(),
    provideTranslateService({
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpBackend]
      },
      missingTranslationHandler: { provide: MissingTranslationHandler, useClass: LibMissingTranslationHandler },
      defaultLanguage: 'th',
    }),
    { provide: coreUserApiClientConfigToken, useFactory: initApiClientConfig },
    { provide: CoreApiErrorHandlerService, useClass: CustomApiErrorHandlerService },
    { provide: coreUserAuthServiceConfigToken, useFactory: initAuthServiceConfig },
    { provide: coreUserAuthInterceptorConfigToken, useFactory: initAuthInterceptorConfigToken },
    { provide: coreUserAuthResponseKeyToken, useValue: authResponseKey },
    { provide: coreUserStorageConfigToken, useFactory: initStorageConfig },
    ConfirmationService,
    PopupService,
    TranslateService,
    CoreTranslateService,
    CoreRouteService,
    GlobalDataService,
    MessageService
  ]
};
